import React, { useEffect, useMemo } from 'react';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { Box, Chip, Stack, Tooltip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { setSelected } from '../../../modules/conteneurMap/conteneurMap.slice';
import {
    getDechetTypeLabel,
    getChecklistColor,
    getChecklistEtatLabel,
    getChecklistElementsEnDefaut,
} from '../../../modules/conteneurMap/conteneurMap.helpers';
import DechetTypeChip from '../layout/DechetTypeChip';
import ConteneurTypeChip from '../layout/ConteneurTypeChip';
import ConteneurStatusBadge from '../layout/ConteneurStatusBadge';

const formatDateRelative = (iso) => {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleDateString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
        });
    } catch (e) {
        return iso;
    }
};


const TypeBadges = ({ conteneurType, dechetType }) => (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <ConteneurTypeChip
            type={conteneurType}
            label={conteneurType}
        />
        <DechetTypeChip
            type={dechetType}
            label={getDechetTypeLabel(dechetType)}
        />
    </Box>
);

/** Cellule « État maintenance » (PAT-04) : chip colorée + éléments en défaut ou raison en tooltip. */
const ChecklistCell = ({ checklist }) => {
    const etat = checklist?.etat ?? 'NON_INSPECTE';
    const enDefaut = getChecklistElementsEnDefaut(checklist).map((e) => e.libelle ?? e.code);
    const detail = etat === 'TENTATIVE_IMPOSSIBLE' ? checklist?.raison : enDefaut.join(', ');
    return (
        <Tooltip title={detail || ''} disableHoverListener={!detail}>
            <Chip
                size="small"
                label={enDefaut.length > 1 ? `${getChecklistEtatLabel(etat)} (${enDefaut.length})` : getChecklistEtatLabel(etat)}
                sx={{ backgroundColor: getChecklistColor(checklist), color: 'white', fontWeight: 600 }}
            />
        </Tooltip>
    );
};

/**
 * Liste DataGrid synchronisée avec la carte.
 * `rows` est passé en prop (déjà filtré par le parent via Reselect).
 * `checklistMode` (PAT-04) : remplace les colonnes Statut / Dernier lavage par État maintenance / Inspecté le.
 */
function ConteneurList({ rows, checklistMode = false }) {
    const apiRef = useGridApiRef();
    const dispatch = useDispatch();
    const selectedId = useSelector((s) => s.conteneurMap.selectedId);

    const columns = useMemo(() => ([
        { field: 'nomenclature', headerName: 'ID', width: 110 },
        { field: 'patrimoineLibelle', headerName: 'Patrimoine', flex: 1, minWidth: 140 },
        { field: 'adresse', headerName: 'Adresse', flex: 1.2, minWidth: 160 },
        { field: 'commune', headerName: 'Commune', flex: 1, minWidth: 130 },
        {
            field: 'conteneurType',
            headerName: 'Type',
            width: 180,
            sortable: false,
            renderCell: (params) => (
                <TypeBadges
                    conteneurType={params.row.conteneurType}
                    dechetType={params.row.dechetType}
                />
            ),
        },
        { field: 'quantite', headerName: 'Qté', width: 70, type: 'number' },
        ...(checklistMode ? [
            {
                field: 'checklistEtat',
                headerName: 'État maintenance',
                width: 170,
                valueGetter: (_value, row) => row.checklist?.etat ?? 'NON_INSPECTE',
                renderCell: (params) => <ChecklistCell checklist={params.row.checklist} />,
            },
            {
                field: 'checklistDate',
                headerName: 'Inspecté le',
                width: 120,
                valueGetter: (_value, row) => row.checklist?.date ?? null,
                valueFormatter: (value) => formatDateRelative(value),
            },
        ] : [
            {
                field: 'status',
                headerName: 'Statut',
                width: 130,
                renderCell: (params) => <ConteneurStatusBadge status={params.value} />,
            },
            {
                field: 'dernierLavageAt',
                headerName: 'Dernier lavage',
                width: 130,
                valueFormatter: (value) => formatDateRelative(value),
            },
        ]),
    ]), [checklistMode]);

    // Scroll vers la ligne sélectionnée
    useEffect(() => {
        if (selectedId == null) return;
        const rowIndex = rows.findIndex((r) => r.id === selectedId);
        if (rowIndex === -1) return;
        try {
            apiRef.current?.scrollToIndexes?.({ rowIndex });
        } catch (e) {
            // pas grave si l'API DataGrid n'est pas encore prête
        }
    }, [selectedId, rows, apiRef]);

    const handleRowClick = (params) => {
        dispatch(setSelected(params.row.id));
    };

    return (
        <Stack sx={{ height: '100%', width: '100%' }} spacing={1}>
            <div style={{ flex: 1, minHeight: 0 }}>
                <DataGrid
                    apiRef={apiRef}
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.id}
                    onRowClick={handleRowClick}
                    rowSelectionModel={selectedId != null ? [selectedId] : []}
                    density="compact"
                    disableColumnMenu
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 50 } },
                    }}
                    sx={{
                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                    }}
                />
            </div>
        </Stack>
    );
}

export default ConteneurList;
